import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mapStatusToBadge } from '../lib/status';
import { Alert, Button, FilterBar, MetricCard, Modal, PageHeader, StatusBadge } from '../components/ui';
import {
  adminWorkflowsService,
  ComplianceRecord,
  ComplianceStatus,
} from '../services/admin-workflows.service';

type ViewMode = 'grid' | 'list';
type SortMode = 'risk-high' | 'risk-low' | 'expiry-nearest' | 'title';

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

export default function AdminCompliancePage() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<ComplianceRecord[]>([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ComplianceStatus>('all');
  const [sortBy, setSortBy] = useState<SortMode>('risk-high');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [activeRecord, setActiveRecord] = useState<ComplianceRecord | null>(null);
  const [exceptionReason, setExceptionReason] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadRecords = async () => {
      try {
        setLoading(true);
        const data = await adminWorkflowsService.getComplianceRecords({
          sortBy: 'risk-high',
        });

        if (!active) {
          return;
        }

        setRecords(data);
        setError(null);
      } catch (loadError) {
        if (!active) {
          return;
        }

        setError((loadError as Error).message || 'Failed to load compliance records');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadRecords();

    return () => {
      active = false;
    };
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All', count: records.length },
      { value: 'active', label: 'Active', count: records.filter((item) => item.status === 'active').length },
      {
        value: 'suspicious',
        label: 'Suspicious',
        count: records.filter((item) => item.status === 'suspicious').length,
      },
      { value: 'expired', label: 'Expired', count: records.filter((item) => item.status === 'expired').length },
      { value: 'disabled', label: 'Disabled', count: records.filter((item) => item.status === 'disabled').length },
    ],
    [records]
  );

  const visibleRecords = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = records.filter((item) => {
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesQuery =
        normalizedQuery === '' ||
        item.certificate.toLowerCase().includes(normalizedQuery) ||
        item.region.toLowerCase().includes(normalizedQuery) ||
        item.owner.toLowerCase().includes(normalizedQuery);
      return matchesStatus && matchesQuery;
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'risk-low') {
        return a.riskScore - b.riskScore;
      }
      if (sortBy === 'expiry-nearest') {
        return new Date(a.validUntil).getTime() - new Date(b.validUntil).getTime();
      }
      if (sortBy === 'title') {
        return a.certificate.localeCompare(b.certificate);
      }
      return b.riskScore - a.riskScore;
    });
  }, [query, records, sortBy, statusFilter]);

  const metricSummary = useMemo(() => {
    const expiringSoon = records.filter((item) => {
      if (item.status === 'disabled') {
        return false;
      }
      const expiry = new Date(item.validUntil).getTime();
      const now = Date.now();
      const withinThirtyDays = now <= expiry && expiry - now <= 1000 * 60 * 60 * 24 * 30;
      return withinThirtyDays;
    }).length;

    return {
      active: records.filter((item) => item.status === 'active').length,
      exceptions: records.filter((item) => item.status === 'suspicious').length,
      expiringSoon,
      highRisk: records.filter((item) => item.riskScore >= 70).length,
    };
  }, [records]);

  const applyRecordUpdate = (updatedRecord: ComplianceRecord, message: string) => {
    setRecords((prev) => prev.map((item) => (item.id === updatedRecord.id ? updatedRecord : item)));
    setSuccess(message);
    setError(null);
  };

  const markReviewed = async (recordId: string) => {
    try {
      setActionLoading(`review-${recordId}`);
      const updated = await adminWorkflowsService.reviewComplianceRecord(recordId);
      applyRecordUpdate(updated, 'Compliance record marked as reviewed.');
    } catch (actionError) {
      setError((actionError as Error).message || 'Failed to mark record as reviewed');
    } finally {
      setActionLoading(null);
    }
  };

  const disableRecord = async (recordId: string) => {
    try {
      setActionLoading(`disable-${recordId}`);
      const updated = await adminWorkflowsService.disableComplianceRecord(recordId);
      applyRecordUpdate(updated, 'Compliance record disabled.');
    } catch (actionError) {
      setError((actionError as Error).message || 'Failed to disable record');
    } finally {
      setActionLoading(null);
    }
  };

  const openExceptionModal = (record: ComplianceRecord) => {
    setActiveRecord(record);
    setExceptionReason('');
    setModalError(null);
  };

  const submitException = async () => {
    if (!activeRecord) {
      return;
    }

    if (exceptionReason.trim().length < 8) {
      setModalError('Please add a reason with at least 8 characters.');
      return;
    }

    try {
      setActionLoading(`exception-${activeRecord.id}`);
      const updated = await adminWorkflowsService.logComplianceException(activeRecord.id, exceptionReason.trim());
      applyRecordUpdate(updated, `Exception logged for "${activeRecord.certificate}".`);
      setActiveRecord(null);
      setExceptionReason('');
      setModalError(null);
    } catch (actionError) {
      setModalError((actionError as Error).message || 'Failed to log exception');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <PageHeader
        title="Compliance Workspace"
        description="Certification lifecycle workspace with review and exception action workflows."
        badge={<StatusBadge tone="active">Persisted Workflow</StatusBadge>}
        actions={
          <Button variant="outline" onClick={() => navigate('/admin')}>
            Back
          </Button>
        }
      />

      {success && <Alert variant="success">{success}</Alert>}
      {error && <Alert variant="error">{error}</Alert>}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="Active Certifications" value={metricSummary.active} tone="green" icon="A" />
        <MetricCard title="Open Exceptions" value={metricSummary.exceptions} tone="yellow" icon="E" />
        <MetricCard title="Expiring (30 Days)" value={metricSummary.expiringSoon} tone="blue" icon="30" />
        <MetricCard title="High Risk (70+)" value={metricSummary.highRisk} tone="red" icon="!" />
      </section>

      <FilterBar
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search certificate, region, or owner..."
        statusOptions={statusOptions}
        selectedStatus={statusFilter}
        onStatusChange={(value) => setStatusFilter(value as 'all' | ComplianceStatus)}
        sortOptions={[
          { value: 'risk-high', label: 'Sort: Risk High-Low' },
          { value: 'risk-low', label: 'Sort: Risk Low-High' },
          { value: 'expiry-nearest', label: 'Sort: Expiry Nearest' },
          { value: 'title', label: 'Sort: Certificate A-Z' },
        ]}
        selectedSort={sortBy}
        onSortChange={(value) => setSortBy(value as SortMode)}
        viewOptions={[
          { value: 'grid', label: 'View: Grid' },
          { value: 'list', label: 'View: List' },
        ]}
        selectedView={viewMode}
        onViewChange={(value) => setViewMode(value as ViewMode)}
      />

      {loading ? (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm text-sm text-gray-600">Loading compliance records...</section>
      ) : viewMode === 'grid' ? (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleRecords.map((record) => {
            const statusBadge = mapStatusToBadge(record.status);
            return (
              <article key={record.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-base font-semibold text-gray-900">{record.certificate}</h2>
                  <StatusBadge tone={statusBadge.tone} size="sm">
                    {statusBadge.label}
                  </StatusBadge>
                </div>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs font-semibold uppercase text-gray-500">Region</dt>
                    <dd className="text-gray-900">{record.region}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase text-gray-500">Owner</dt>
                    <dd className="text-gray-900">{record.owner}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase text-gray-500">Valid Until</dt>
                    <dd className="text-gray-900">{formatDate(record.validUntil)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase text-gray-500">Risk Score</dt>
                    <dd className="text-gray-900">{record.riskScore}/100</dd>
                  </div>
                </dl>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markReviewed(record.id)}
                    disabled={actionLoading !== null}
                  >
                    Mark Reviewed
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openExceptionModal(record)}
                    disabled={actionLoading !== null}
                  >
                    Log Exception
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => disableRecord(record.id)}
                    disabled={actionLoading !== null}
                  >
                    Disable
                  </Button>
                </div>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" aria-label="Compliance records">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Certificate</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Region</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Owner</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Risk</th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visibleRecords.map((record) => {
                  const statusBadge = mapStatusToBadge(record.status);
                  return (
                    <tr key={record.id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{record.certificate}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.region}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.owner}</td>
                      <td className="px-4 py-3">
                        <StatusBadge tone={statusBadge.tone} size="sm">
                          {statusBadge.label}
                        </StatusBadge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{record.riskScore}/100</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markReviewed(record.id)}
                            disabled={actionLoading !== null}
                          >
                            Review
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openExceptionModal(record)}
                            disabled={actionLoading !== null}
                          >
                            Exception
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => disableRecord(record.id)}
                            disabled={actionLoading !== null}
                          >
                            Disable
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Modal
        isOpen={activeRecord !== null}
        onClose={() => {
          setActiveRecord(null);
          setModalError(null);
          setExceptionReason('');
        }}
        title="Log Compliance Exception"
      >
        <div className="space-y-4">
          {activeRecord && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              <p className="font-medium text-gray-900">{activeRecord.certificate}</p>
              <p>{activeRecord.region}</p>
            </div>
          )}
          {modalError && <Alert variant="error">{modalError}</Alert>}
          <label className="block text-sm font-medium text-gray-700">
            Exception reason
            <textarea
              value={exceptionReason}
              onChange={(event) => setExceptionReason(event.target.value)}
              rows={4}
              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              placeholder="Describe why this compliance record requires escalation..."
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setActiveRecord(null)}>
              Cancel
            </Button>
            <Button onClick={submitException} disabled={actionLoading !== null}>
              Submit Exception
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
